import { useCallback, useRef, useState } from "react";

import { describeWalletError } from "@/lib/walletError";

/**
 * What the account's private channels are waiting on, if anything.
 *
 * The delegated key used to be managed invisibly: infer from local deposit
 * history whether an account ought to exist, auto-prompt the wallet, spend a
 * once-per-session budget, throttle, and retry in the background. Every failure
 * looked the same from the outside -- including a wallet popup opening behind
 * the window -- so a stuck session was indistinguishable from a slow one.
 *
 * This is the same lifecycle as explicit states, driven by the server's own
 * answer (`account_not_found` vs `key_not_registered`) rather than by guessing
 * from localStorage, and surfaced so the user can see and act on it.
 */
export type DelegatedKeyStatus =
  | "ready" // private channels subscribed; nothing to do
  | "waiting-for-account" // no ledger account yet -- a deposit must absorb first
  | "needs-signature" // account exists, this key is not registered to it
  | "signing" // waiting on the wallet, which may have opened behind the window
  | "registering" // signed and submitted; waiting for the channels to come up
  | "failed"; // the attempt errored; the message says why

/**
 * The states where the account cannot be read until the user does something.
 *
 * `waiting-for-account` is absent on purpose: a deposit has to absorb first and
 * no signature hurries it, so there is no action to demand. `ready` needs
 * nothing.
 *
 * Shared, because two places have to agree on it -- the modal that raises the
 * prompt, and the trade ticket, whose call to action is otherwise driven by a
 * balance that reads as zero when the truth is that it cannot be read at all.
 */
export const DELEGATED_KEY_NEEDS_USER: DelegatedKeyStatus[] = [
  "needs-signature",
  "signing",
  "registering",
  "failed",
];

export const delegatedKeyNeedsUser = (status: DelegatedKeyStatus): boolean =>
  DELEGATED_KEY_NEEDS_USER.includes(status);

export interface DelegatedKeyState {
  status: DelegatedKeyStatus;
  /** Present when `status` is `failed`. */
  error?: string;
}

/**
 * Own the delegated-key lifecycle for the session.
 *
 * Deliberately never prompts on its own. An auto-prompt races the
 * order-encryption handshake (the wallet call no-ops until that context
 * exists), fires while a deposit is still absorbing and cannot succeed, and
 * gives the user no way to retry when a popup is missed. Requiring a click
 * removes all three: the button is disabled until signing can actually work,
 * and pressing it is always meaningful.
 *
 * @param canSign
 *   whether the signing context is ready. Gates the button rather than failing
 *   an attempt.
 * @param register
 *   perform the registration: sign with the main wallet and submit. Resolves
 *   false if it could not attempt.
 */
export function useDelegatedKeyStatus(
  canSign: boolean,
  register: () => Promise<boolean>,
) {
  const [state, setState] = useState<DelegatedKeyState>({ status: "ready" });
  // Whether an attempt is being watched, so a repeat of the same rejection does
  // not stomp a `signing` / `registering` state the user is looking at.
  const inProgress = useRef(false);
  // Identifies the current attempt. A wallet promise cannot be cancelled, so
  // dismissing one only stops us WAITING on it; if it resolves later it must
  // not move a state the user has since moved on from. Bumping this on every
  // start and dismissal makes a stale resolution detectable.
  const attemptId = useRef(0);

  /** Fold a private-auth rejection into the state. */
  const onAuthError = useCallback((code: string) => {
    if (inProgress.current) return;
    setState(
      code === "account_not_found"
        ? { status: "waiting-for-account" }
        : { status: "needs-signature" },
    );
  }, []);

  /** Private channels are live: whatever we were waiting on is done. */
  const onAuthSuccess = useCallback(() => {
    inProgress.current = false;
    setState({ status: "ready" });
  }, []);

  /** User-initiated. Registers the key and leaves the state in `registering`
   *  until the channels come up (or `failed`). */
  const signAndRegister = useCallback(async () => {
    if (!canSign || inProgress.current) return;
    inProgress.current = true;
    const attempt = ++attemptId.current;
    setState({ status: "signing" });
    try {
      const attempted = await register();
      if (attempt !== attemptId.current) return; // dismissed while we waited
      if (!attempted) {
        // Preconditions vanished between the button enabling and the click.
        inProgress.current = false;
        setState({ status: "needs-signature" });
        return;
      }
      // Registration is submitted, not yet visible: the ledger has to include
      // it in a block before the channels will accept our signature. Hold
      // `registering` and let onAuthSuccess clear it.
      setState({ status: "registering" });
    } catch (e: unknown) {
      if (attempt !== attemptId.current) return;
      inProgress.current = false;
      setState({
        status: "failed",
        error: describeWalletError(e),
      });
    }
  }, [canSign, register]);

  /**
   * Stop waiting on the current attempt and offer the button again.
   *
   * A wallet popup can be closed, or open behind the window and never be
   * answered, and neither produces a rejection we can observe -- so without
   * this the card would sit on "waiting for your wallet" with a disabled
   * button, which is the dead end this whole rework exists to remove. The
   * underlying promise is left to settle into nothing.
   */
  const dismiss = useCallback(() => {
    attemptId.current += 1;
    inProgress.current = false;
    setState({ status: "needs-signature" });
  }, []);

  return {
    state,
    canSign,
    onAuthError,
    onAuthSuccess,
    signAndRegister,
    dismiss,
  };
}
